import {
  Avatar,
  Button,
  createStyles,
  Menu,
  Modal,
  SimpleGrid,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import React from "react";
import { useContext } from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { THEME } from "../../Actions/stateActions";
import { handleError } from "../../AppCommon/util";
import { AppContext, TOGGLE_SETTINGS } from "../../Context";
import { UserManagerContext } from "../../Context/userManagerContext";
import {
  AccountIcon,
  BookingIcon,
  LogoutIcon,
  SettingsIcon,
} from "./icons/Icons";

const useStyles = createStyles((theme) => ({
  settingWrapper: {
    paddingRight: "1rem",
    button: {
      borderRadius: "6px",
    },
  },
  button: {
    boxShadow: `0 0 0 3px ${theme.colors.violet[8]}`,
    borderRadius: "50%",
    "*::WebkitBorderRadius": "50%",
    ":hover": {
      opacity: 0.9,
    },
  },

  dropdownWrapper: {
    "@media screen and (max-width: 768px)": {
      padding: 0,
    },
  },

  item: {
    "&[data-hovered]": {
      backgroundColor: theme.colors.violet[8],
      color: "white",
    },
  },

  mainSection: {
    paddingBottom: theme.spacing.sm,
  },

  userActive: {
    opacity: 0.9,
  },

  darkMode: {
    backgroundColor: "#212121",
    borderColor: theme.colors.dark[7],
    button: {
      color: theme.colors.dark[0],
      "&:hover": {
        backgroundColor: theme.colors.dark[8],
      },
    },
    ".mantine-Menu-divider": {
      borderColor: theme.colors.dark[5],
    },
  },

  darkestMode: {
    backgroundColor: "#0C1217",
    borderColor: theme.colors.dark[7],
    button: {
      color: theme.colors.dark[0],
      "&:hover": {
        backgroundColor: theme.colors.dark[6],
      },
    },
    ".mantine-Menu-divider": {
      borderColor: theme.colors.dark[5],
    },
  },
}));

const Profile = ({ whoami }) => {
  const [userMenuOpened, setUserMenuOpened] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { language, config, auth, theme } = useSelector((state) => ({
    language: state.language,
    config: state.config,
    auth: state.oidc.user !== null && !state.oidc.user.expired,
    theme: state.cookies[THEME],
  }));
  const context = useContext(AppContext);
  const { dispatch } = context;
  const { userManager } = useContext(UserManagerContext);

  const history = useHistory();
  const { classes, cx } = useStyles();
  let user = {
    name: whoami.name,
    image: whoami.photo,
  };

  const membersLink = `${
    config.api.split("bookingapi")[0]
  }members?redirect=loginRedirectFromBooking`;

  const handleLogout = () => userManager.signoutRedirect().catch(handleError);
  return (
    <>
      {auth ? (
        <Menu
          width={200}
          position="bottom-start"
          transition="pop-top-right"
          onClose={() => setUserMenuOpened(false)}
          onOpen={() => setUserMenuOpened(true)}
          shadow="md"
          className={cx(classes.dropdownWrapper)}
          classNames={classes}
        >
          <Menu.Target>
            <UnstyledButton aria-label={language.account_menu}>
              <Avatar
                src={user.image}
                alt={language.profilePicture}
                size={"md"}
                style={{ borderRadius: "50%" }}
                className={cx(classes.button, {
                  [classes.userActive]: userMenuOpened,
                })}
                role="img"
                aria-label={language.account_menu}
              />
            </UnstyledButton>
          </Menu.Target>
          <div style={{ position: "relative", marginRight: "1rem" }}>
            <Menu.Dropdown
              className={cx(
                classes.dropdown,
                {
                  [classes.darkMode]: theme === "dark" ? true : false,
                },
                {
                  [classes.darkestMode]: theme === "darkest" ? true : false,
                }
              )}
            >
              <Menu.Item
                icon={<BookingIcon />}
                onClick={() => history.push("/bookings")}
                aria-label={language.my_bookings}
              >
                {language.my_bookings}
              </Menu.Item>
              <Menu.Item
                icon={<AccountIcon />}
                onClick={() => (window.location.href = membersLink)}
                aria-label={language.my_account}
              >
                {language.my_account}
              </Menu.Item>

              <Menu.Item
                icon={<SettingsIcon />}
                onClick={() => dispatch({ type: TOGGLE_SETTINGS })}
                aria-label={language.settings}
              >
                {language.settings}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                onClick={() => setShowLogoutDialog(true)}
                icon={<LogoutIcon />}
                aria-label={language.logout}
              >
                {language.logout}
              </Menu.Item>
            </Menu.Dropdown>
          </div>
        </Menu>
      ) : (
        <div className={classes.settingWrapper}>
          <UnstyledButton
            aria-label={language.settings}
            onClick={() => dispatch({ type: TOGGLE_SETTINGS })}
            style={{
              color:
                theme === "dark"
                  ? "#fff"
                  : theme === "darkest"
                  ? "#fff"
                  : theme === "light"
                  ? "#000"
                  : "inherit",
            }}
          >
            <SettingsIcon />
          </UnstyledButton>
        </div>
      )}
      <Modal
        size="31.25rem"
        opened={showLogoutDialog}
        centered
        onClose={() => setShowLogoutDialog(false)}
        styles={{ header: { margin: 0 } }}
        padding="xl"
        radius="md"
        zIndex={1500}
        classNames={{
          modal:
            theme === "dark"
              ? classes.darkMode
              : theme === "darkest"
              ? classes.darkestMode
              : "",
        }}
        closeButtonLabel={language.closeMenus}
      >
        <SimpleGrid
          spacing="xl"
          style={{
            color: theme === "dark" || theme === "darkest" ? "white" : "black",
          }}
        >
          <Title align="center" order={1} style={{ fontSize: "1.25rem" }}>
            {language.logout}
          </Title>
          <SimpleGrid cols={1} spacing="xs">
            <Text align="center">{language.logoutMsg}</Text>
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <Button
              size="md"
              variant="outline"
              color="violet.8"
              onClick={() => setShowLogoutDialog(false)}
              aria-label={language.abort}
            >
              {language.abort}
            </Button>
            <Button
              size="md"
              color="violet.8"
              onClick={handleLogout}
              style={{ color: "white" }}
              aria-label={language.logout}
            >
              {language.logout}
            </Button>
          </SimpleGrid>
        </SimpleGrid>
      </Modal>
    </>
  );
};

export default Profile;
